import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TraitBars } from "../components/Charts";
import ShareButton from "../components/ShareButton";
import ContactModal from "../components/ContactModal";
import { Users, Send, Loader2, RefreshCw, CheckCircle2, Clock, Sparkles, Puzzle, AlertTriangle, ListChecks, Download, Bell, Crown, Zap, Anchor, SearchCheck } from "lucide-react";

const ROLES = ["teammate", "manager", "direct report", "peer", "colleague", "lead"];
const REMINDER_PRESETS = [
  { label: "Nudge at day 1 & day 7", days: [1, 7] },
  { label: "Nudge at day 3 & day 7", days: [3, 7] },
  { label: "Nudge at day 7 only", days: [7] },
  { label: "Nudge at day 1, 3 & 7", days: [1, 3, 7] },
];
const ROLE_ICON = { Leads: Crown, Ideates: Zap, Steadies: Anchor, Refines: SearchCheck };

function Card({ children, className = "" }) {
  return <div className={`bg-white border border-[#E8E1D9] rounded-2xl p-6 sm:p-8 shadow-sm ${className}`}>{children}</div>;
}
function BulletList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items?.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-[#2B2625]">
          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-[15px] leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}
function Skeleton({ lines = 3 }) {
  return (
    <div className="space-y-2.5 animate-pulse" data-testid="team-skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3.5 rounded-full bg-[#EFE7DD]" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "teammate" });
  const [sending, setSending] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try { setTeam(await api.getTeam(id)); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    let alive = true;
    const tick = async () => {
      try {
        const t = await api.getTeam(id);
        if (!alive) return;
        setTeam(t);
      } catch { /* ignore */ }
      if (alive) timer.current = setTimeout(tick, 5000);
    };
    tick();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [id, user, navigate]);

  const invite = async () => {
    if (!form.email) { toast.error("Add their email"); return; }
    setSending(true);
    try {
      await api.inviteTeam(id, form);
      toast.success("Invitation sent!");
      setForm({ email: "", name: "", role: "teammate" });
      load();
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.detail === "team_full") {
        setContactOpen(true);
      } else {
        toast.error(err?.response?.data?.detail || "Could not send");
      }
    } finally { setSending(false); }
  };

  const changeReminders = async (days) => {
    try { await api.setReminderDays(id, days); toast.success("Reminder schedule updated"); load(); }
    catch { toast.error("Could not update reminders"); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try { await api.downloadTeamPdf(id); }
    catch { toast.error("Snapshot PDF isn't ready yet"); }
    finally { setPdfLoading(false); }
  };

  const refresh = async () => {
    try { await api.refreshTeam(id); toast.success("Refreshing snapshot…"); load(); }
    catch { toast.error("Could not refresh"); }
  };

  if (!team) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[#918780]">Loading team…</div>;

  const snap = team.snapshot;
  const nar = team.narrative;
  const ready = team.report_status === "ready" && snap && nar;
  const completed = team.members.filter((m) => m.status === "completed").length;
  const capacity = team.capacity || 5;
  const isFull = team.members.length >= capacity;
  const field = "w-full px-4 py-3 rounded-xl border border-[#E8E1D9] bg-white focus:outline-none focus:border-[#6B21A8] focus:ring-2 focus:ring-[#6B21A8]/15 transition";

  return (
    <div className="max-w-4xl mx-auto px-5 py-10 space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl bg-[#3F3A44] text-white p-8 sm:p-11 text-center">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#E4D6F5]">Team snapshot</span>
        <h1 className="font-display text-2xl sm:text-4xl font-bold mt-2" data-testid="team-title">{team.name}</h1>
        {ready ? (
          <div className="mt-5 inline-flex flex-col items-center">
            <span className="font-display text-6xl sm:text-7xl font-bold text-[#7C3AED]" data-testid="team-synergy">{snap.synergy}%</span>
            <span className="font-semibold text-white/90 mt-1">{snap.band}</span>
          </div>
        ) : (
          <p className="text-[#E4D6F5] mt-4">{completed < 2 ? "Invite at least one member who completes the assessment to reveal your snapshot." : "Building your team snapshot…"}</p>
        )}
        {ready && (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <ShareButton url={`/team/${id}`} variant="dark" title={`${team.name} — ${snap.synergy}% team synergy`}
              text={`Our team ${team.name} scored ${snap.synergy}% synergy on RelateMetrics! See how we work together:`} />
            <button onClick={downloadPdf} disabled={pdfLoading} data-testid="team-download-pdf"
              className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold py-3 px-5 rounded-xl transition-colors disabled:opacity-60">
              {pdfLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} PDF
            </button>
          </div>
        )}
      </motion.div>

      {/* Members + invite */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#2B2625] font-semibold">
            <Users size={18} /> Members <span className="text-[#918780]" data-testid="team-capacity">({team.members.length}/{capacity})</span>
          </div>
          {completed >= 2 && (
            <button onClick={refresh} data-testid="team-refresh"
              className="inline-flex items-center gap-1.5 text-sm text-[#3F3A44] font-semibold hover:text-[#35313A]">
              <RefreshCw size={15} /> Refresh
            </button>
          )}
        </div>
        <div className="space-y-2 mb-6">
          {team.members.map((m, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0EAE1] last:border-0">
              <div>
                <span className="font-semibold text-[#2B2625]">{m.name}</span>
                <span className="text-sm text-[#918780] ml-2 capitalize">{m.role}</span>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                m.status === "completed" ? "bg-[#F2E9FA] text-[#3F3A44]" : "bg-[#FEF6E8] text-[#3B0764]"}`}>
                {m.status === "completed" ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                {m.status === "completed" ? "Done" : "Invited"}
              </span>
            </div>
          ))}
        </div>

        {isFull ? (
          <div className="rounded-xl bg-[#FEF6E8] text-[#3B0764] px-4 py-4 text-sm">
            <p className="font-semibold mb-2">Your team is full (up to {capacity}).</p>
            <button data-testid="team-contact-us" onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors">
              Need a bigger team? Contact us
            </button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs font-semibold text-[#918780] block mb-1">Email</label>
                <input data-testid="team-invite-email" className={field} value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@work.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#918780] block mb-1">Name (optional)</label>
                <input data-testid="team-invite-name" className={field} value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#918780] block mb-1">Role</label>
            <select data-testid="team-invite-role" className={field} value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <button data-testid="team-invite-send" onClick={invite} disabled={sending}
          className="mt-4 inline-flex items-center justify-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3 px-6 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Invite member</>}
        </button>
          </>
        )}

        <div className="mt-6 pt-5 border-t border-[#F0EAE1]">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#2B2625] mb-2">
            <Bell size={15} /> Gentle reminders for people who haven't finished
          </label>
          <select data-testid="team-reminder-select" className={field}
            value={JSON.stringify(team.reminder_days || [1, 7])}
            onChange={(e) => changeReminders(JSON.parse(e.target.value))}>
            {REMINDER_PRESETS.map((p) => (
              <option key={p.label} value={JSON.stringify(p.days)}>{p.label}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Roles view */}
      {completed >= 2 && snap?.members && (
        <Card>
          <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-1">Who plays which role</h3>
          <p className="text-sm text-[#918780] mb-5">A quick read on your team's natural shape.</p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="team-roles">
            {snap.members.map((m, i) => {
              const Icon = ROLE_ICON[m.role] || Sparkles;
              return (
                <div key={i} className="flex items-start gap-3 bg-[#FBF6F0] rounded-xl p-4">
                  <span className="w-10 h-10 rounded-xl bg-white text-[#6B21A8] grid place-items-center shrink-0"><Icon size={18} /></span>
                  <div>
                    <p className="font-semibold text-[#2B2625]">{m.name} · <span className="text-[#6B21A8]">{m.role}</span></p>
                    <p className="text-sm text-[#6E655F] capitalize">{m.role_tag} — {m.role_desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Snapshot */}
      {completed >= 2 && (
        <>
          {snap?.style_mix && (
            <Card>
              <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-4">Your team's style mix</h3>
              <div className="flex flex-wrap gap-3">
                {snap.style_mix.map((s) => (
                  <div key={s.style} className="flex items-center gap-2 bg-[#F4EAE1] rounded-xl px-4 py-2.5">
                    <span className="font-semibold text-[#2B2625]">{s.style}</span>
                    <span className="text-sm text-[#6B21A8] font-bold">{s.count}</span>
                  </div>
                ))}
              </div>
              {snap.missing_styles?.length > 0 && (
                <p className="text-sm text-[#918780] mt-4">Not represented yet: {snap.missing_styles.join(", ")}</p>
              )}
            </Card>
          )}

          {snap?.team_areas && (
            <Card>
              <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-5">How you collaborate</h3>
              <TraitBars items={Object.keys(snap.team_areas).map((k) => ({ label: k, value: snap.team_areas[k] }))} color="#3F3A44" />
            </Card>
          )}

          <Card>
            <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-3"><Sparkles size={17} /> How your team works together</div>
            {ready ? <p className="text-[#2B2625] text-lg leading-relaxed">{nar.overview}</p> : <Skeleton lines={3} />}
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-4"><Puzzle size={17} /> Collaboration strengths</div>
              {ready ? <BulletList items={nar.collaboration_strengths} color="#3F3A44" /> : <Skeleton lines={3} />}
            </Card>
            <Card>
              <div className="flex items-center gap-2 text-[#7C3AED] font-semibold mb-4"><AlertTriangle size={17} /> Watch-outs</div>
              {ready ? <BulletList items={nar.watch_outs} color="#7C3AED" /> : <Skeleton lines={3} />}
            </Card>
          </div>

          <Card>
            <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-4"><ListChecks size={17} /> Recommendations</div>
            {ready ? <BulletList items={nar.recommendations} color="#6B21A8" /> : <Skeleton lines={4} />}
          </Card>

          {snap?.pairs && (
            <Card>
              <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-4">One-to-one connections</h3>
              <div className="space-y-2">
                {snap.pairs.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0EAE1] last:border-0">
                    <span className="text-[#2B2625]">{p.a} <span className="text-[#918780]">&</span> {p.b}</span>
                    <span className="font-bold text-[#3F3A44]">{p.overall}%</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {ready && (
            <Card className="bg-[#F2E9FA] border-[#E4D6F5]">
              <p className="font-display text-xl sm:text-2xl text-[#3F3A44] italic text-center leading-relaxed">"{nar.closing}"</p>
            </Card>
          )}
        </>
      )}

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
